import { useCallback, useRef, useState } from 'react';
import {
  dependabilityV2API,
  DependabilityEvent,
} from '@/services/dependabilityV2API';

// Shared event-history loader for the card feed and the activity modal.
const useDependabilityEvents = (workerId: number, perPage: number) => {
  const [events, setEvents] = useState<DependabilityEvent[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  // false until the first load settles — lets hosts render "not fetched yet"
  // as loading instead of flashing the empty state before the fetch effect
  const [loaded, setLoaded] = useState(false);
  // distinct from the empty state: a swallowed fetch failure must surface as a
  // retryable error, not collapse into "No score activity yet"
  const [error, setError] = useState(false);
  // The page a retry must re-fetch is the one that was actually last ATTEMPTED,
  // not one inferred from persisted events.length/page. Hook state survives a
  // modal close/reopen, so after reopen->loadPage(1) fails the stale prior-session
  // `page` (e.g. 3) would make an events.length-based retry target page 4 and
  // APPEND to the stale list instead of refreshing page 1.
  const lastAttemptedPageRef = useRef(1);

  const loadPage = useCallback(
    async (nextPage: number) => {
      lastAttemptedPageRef.current = nextPage;
      setLoading(true);
      setError(false);
      try {
        const res = await dependabilityV2API.getEvents(
          workerId,
          nextPage,
          perPage,
        );
        if (res.success) {
          // Dedupe by id (rev 5): offset pagination shifts when new events land
          // between page loads — without this, a live insert makes page N+1
          // re-serve the tail of page N (duplicate keys, repeated rows).
          setEvents((prev) =>
            nextPage === 1
              ? res.data
              : [
                  ...prev,
                  ...res.data.filter((e) => !prev.some((p) => p.id === e.id)),
                ],
          );
          setPage(res.pagination.page);
          setTotalPages(res.pagination.total_pages);
        }
      } catch {
        // the feed is non-critical; its host keeps rendering. Flag the error so
        // the host can offer a retry instead of showing a false empty state.
        setError(true);
      } finally {
        setLoading(false);
        setLoaded(true);
      }
    },
    [workerId, perPage],
  );

  // Retry the page that actually failed (page 1 on a failed reopen refresh -> the
  // replace branch clears the stale list; page N+1 on a failed "View more" -> the
  // append branch, no collapse), independent of persisted events.length/page.
  const retry = useCallback(
    () => loadPage(lastAttemptedPageRef.current),
    [loadPage],
  );

  return {
    events,
    page,
    loading,
    loaded,
    error,
    loadPage,
    retry,
    hasMore: page < totalPages,
  };
};

export default useDependabilityEvents;
